/** Hàm thuần của màn đầu buổi sáng (KH-GĐ2 4.2) — tách khỏi component để test không cần trình duyệt. */
import type { DayAppointment, LichChuaDongTruoc, StaffColumn } from './api.ts'
import { T } from './text/vi.ts'

/** Phần của một lịch mà việc tính "trễ" cần — Lịch, Danh sách, Chi tiết đều có đủ. */
type LichTre = Pick<DayAppointment, 'status' | 'startMin' | 'endMin'>

/** Quá giờ hẹn bấy nhiêu phút mà khách chưa tới → "Trễ" (KH-GĐ2 4.2.5; tính lúc vẽ, không lưu). */
const PHUT_TRE = 10

/** `bayGio` = phút hiện tại khi đang xem HÔM NAY, `null` với ngày khác. */
export function laTre(a: LichTre, bayGio: number | null): boolean {
  return bayGio !== null && a.status === 'booked' && bayGio > a.startMin + PHUT_TRE
}

/**
 * "Không đến" chỉ bấm được từ ĐÚNG giờ hẹn trở đi (BK-3a, Q4): ngày đã qua → luôn được; hôm nay → từ `startMin`; ngày sau → không.
 * Chỉ để ẨN nút — máy chủ là người quyết (nó trả 400 nếu đồng hồ lệch). `bayGio` = phút hiện tại (chỉ dùng khi `ngay === homNay`).
 */
export function duocBamKhongDen(
  a: Pick<DayAppointment, 'startMin'>,
  ngay: string,
  homNay: string,
  bayGio: number,
): boolean {
  if (ngay < homNay) return true
  return ngay === homNay && bayGio >= a.startMin
}

/** K9: lịch CŨ NHẤT trong dải "ngày trước chưa thu" (theo ngày rồi giờ) — nút "Xem" nhảy tới đúng lịch đó. `null` khi dải rỗng. */
export function lichCuNhatChuaDong(ds: readonly LichChuaDongTruoc[]): LichChuaDongTruoc | null {
  let cu: LichChuaDongTruoc | null = null
  for (const x of ds) {
    if (cu === null || x.ngay < cu.ngay || (x.ngay === cu.ngay && x.startMin < cu.startMin)) cu = x
  }
  return cu
}

/** Trễ bao nhiêu phút so với giờ hẹn (0 nếu không trễ) — nhãn ghi rõ "Trễ 25 phút", không chỉ "Trễ". */
export function phutTre(a: LichTre, bayGio: number | null): number {
  return bayGio !== null && laTre(a, bayGio) ? bayGio - a.startMin : 0
}

/** Lịch tính tiền được: khách đang ở tiệm, hoặc đã xong mà chưa thu. Mỗi lịch một dòng (nhiều dịch vụ gộp). */
export function lichChoThu(ds: DayAppointment[]): DayAppointment[] {
  const theoLich = new Map<string, DayAppointment[]>()
  for (const a of ds) {
    const dangO = a.status === 'checked_in' || a.status === 'in_service'
    const chuaThu = a.status === 'done' && a.daThu === false
    if (!dangO && !chuaThu) continue
    const nhom = theoLich.get(a.appointmentId) ?? []
    nhom.push(a)
    theoLich.set(a.appointmentId, nhom)
  }
  return [...theoLich.values()]
    .map((nhom) => nhom.reduce((dau, a) => (a.startMin < dau.startMin ? a : dau)))
    .sort((x, y) => x.startMin - y.startMin)
}

/**
 * Đã quá cả giờ KẾT THÚC mà khách chưa tới: không còn là "trễ" nữa — nhãn đổi thành "Chưa tới" (ui-critic v29:
 * "Trễ 11 giờ" là câu sai).
 */
export function quaGioHen(a: LichTre, bayGio: number | null): boolean {
  return bayGio !== null && laTre(a, bayGio) && bayGio >= a.endMin
}

/** Nhãn ngắn cho nhãn "Trễ …": dưới 1 giờ ghi phút, từ 1 giờ ghi giờ (nhãn dài đẩy tên khách thành "Kh…"). */
export function nhanTre(phut: number): string {
  return phut < 60 ? `${phut} phút` : `${Math.floor(phut / 60)} giờ`
}

/**
 * Chữ nhãn "trễ" của một lịch — MỘT nơi cho Lịch (khối + thẻ), Danh sách và Chi tiết (ui-critic v45: cùng lịch mà Lịch
 * ghi "Chưa tới" cam, Danh sách chỉ "Đã đặt" hồng). `null` = không trễ.
 */
export function chuNhanTre(a: LichTre, bayGio: number | null): string | null {
  if (!laTre(a, bayGio)) return null
  return quaGioHen(a, bayGio) ? T.notArrived : T.lateBy(nhanTre(phutTre(a, bayGio)))
}

/** Dữ liệu một ngày của bảng lịch — chỉ những trường hai hàm dưới đây cần. */
interface NgayLich {
  openWindows: { startMin: number; endMin: number }[]
  staff: StaffColumn[]
  appointments: Pick<DayAppointment, 'status' | 'staffId'>[]
}

/**
 * Bảng lịch rỗng vì sao — tính trên DỮ LIỆU GỐC của máy chủ, không trên danh sách đã lọc theo vai:
 * máy chủ chỉ trả ca của CHÍNH thợ cho vai kỹ thuật viên, nên `staff` rỗng ở thợ nghĩa là "bạn không có ca"
 * chứ không phải "tiệm chưa xếp ca cho ai" (ui-critic v45: thợ thấy "Xếp ca cho nhân viên trước" trong khi 3 người
 * khác đang trong ca, và bị bảo làm việc của chủ).
 *  - `dong-cua`: ngày chưa có giờ mở cửa · `tho-khong-ca`: thợ, hôm đó không có ca (và không có lịch) ·
 *  - `chua-xep-ca`: chủ / quản lý / lễ tân, tiệm chưa ai có ca · `null`: vẽ bảng.
 */
export type BangRong = 'dong-cua' | 'tho-khong-ca' | 'chua-xep-ca'
export function bangRong(data: NgayLich, laTho: boolean): BangRong | null {
  // K2 (ADR-111): ngày đóng cửa mà VẪN CÒN lịch chưa huỷ thì phải vẽ lưới — khung "Tiệm nghỉ" sẽ giấu đúng những lịch
  // chủ tiệm cần gọi khách đổi ngày (cũng là lỗi cũ khi bỏ giờ một thứ đang có lịch).
  if (data.openWindows.length === 0 && !data.appointments.some((a) => a.status !== 'cancelled')) {
    return 'dong-cua'
  }
  if (data.staff.length > 0) return null
  return laTho ? 'tho-khong-ca' : 'chua-xep-ca'
}

/**
 * Thợ KHÔNG có ca nhưng VẪN có lịch (ca bị gỡ sau khi đã nhận lịch): thêm một cột mang tên thợ, không giờ ca, để
 * lưới / danh sách vẽ lịch của thợ thay vì giấu sau câu "không có ca". Vai khác, hoặc thợ đã có cột: giữ nguyên.
 */
export function themCotChoTho<D extends NgayLich>(data: D, laTho: boolean, tenToi: string): D {
  if (!laTho || data.staff.length > 0) return data
  const cua = data.appointments.find((a) => a.status !== 'cancelled' && a.staffId !== null)
  if (cua === undefined || cua.staffId === null) return data
  return { ...data, staff: [{ staffId: cua.staffId, fullName: tenToi, color: null, shifts: [] }] }
}

/**
 * Khung giờ của lưới lịch (phút từ nửa đêm, đã làm tròn ra giờ chẵn): hợp giờ mở cửa với các lịch chưa huỷ trong ngày.
 * Không bao giờ tính `Math.min()` trên mảng rỗng (ra `Infinity` làm hỏng lưới): không có gì thì dùng 08:00–20:00.
 */
export function khungLuoi(
  openWindows: readonly { startMin: number; endMin: number }[],
  lich: readonly Pick<DayAppointment, 'status' | 'startMin' | 'endMin'>[],
): { tuPhut: number; denPhut: number } {
  const dau = [
    ...openWindows.map((w) => w.startMin),
    ...lich.filter((a) => a.status !== 'cancelled').map((a) => a.startMin),
  ]
  const cuoi = [
    ...openWindows.map((w) => w.endMin),
    ...lich.filter((a) => a.status !== 'cancelled').map((a) => a.endMin),
  ]
  if (dau.length === 0 || cuoi.length === 0) return { tuPhut: 480, denPhut: 1200 }
  return {
    tuPhut: Math.floor(Math.min(...dau) / 60) * 60,
    denPhut: Math.ceil(Math.max(...cuoi) / 60) * 60,
  }
}
